'use client';

import { Phone, PhoneIncoming, User } from 'lucide-react';
import { QueueStatusBadge } from './QueueStatusBadge';
import type { RingingCallEntry } from '../types/calls.types';

interface CentralCallsPanelProps {
  ringingCalls: RingingCallEntry[];
  queueDepth: number;
  onPickup: (callId: string) => void;
  /**
   * True when this staff member is already on a call.
   *
   * The waiting list stays visible — knowing three callers are queued matters
   * even mid-call — but Pick up is disabled and says why, rather than being a
   * live-looking button that would drop the call in progress.
   */
  disabled?: boolean;
}

export function CentralCallsPanel({
  ringingCalls,
  queueDepth,
  onPickup,
  disabled = false,
}: CentralCallsPanelProps) {
  if (ringingCalls.length === 0 && queueDepth === 0) return null;

  return (
    <div
      id="calls_central_panel"
      className="rounded-xl border border-hms-border bg-hms-card p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-hms-text flex items-center gap-2">
          <PhoneIncoming className="w-4 h-4 text-hms-gold" />
          Active Calls
        </h3>
        {/*
          Was a hand-rolled span that showed the queue depth only. It reported
          "2 queued" while two other callers were actively ringing — the number
          that decides whether you drop what you are doing was the one missing.
          QueueStatusBadge shows both counts and turns red when a call is
          ringing rather than merely waiting.
        */}
        <QueueStatusBadge ringingCount={ringingCalls.length} queueDepth={queueDepth} />
      </div>

      {ringingCalls.length === 0 && (
        <p className="text-xs text-hms-textDim py-2">
          No calls ringing right now.
        </p>
      )}

      <div className="space-y-2">
        {ringingCalls.map((call) => (
          <div
            key={call.callId}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-hms-surface border border-hms-border"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-hms-card">
              <User className="w-4 h-4 text-hms-textSub" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-hms-text truncate">
                {call.callerName}
              </p>
              <p className="text-xs text-hms-textDim">
                Booking {call.bookingId}
              </p>
            </div>
            <button
              id={`calls_pickup_btn_${call.callId}`}
              type="button"
              onClick={() => onPickup(call.callId)}
              disabled={disabled}
              title={disabled ? 'Finish your current call to pick this up' : undefined}
              aria-label={`Pick up call from ${call.callerName}`}
              className={
                disabled
                  ? 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-hms-border text-hms-textDim text-xs font-medium cursor-not-allowed'
                  : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-hms-green text-white text-xs font-medium hover:bg-hms-green/90 transition-colors'
              }
            >
              <Phone className="w-3 h-3" />
              Pick up
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
